import { useMemo } from 'react';

import { Box, Flex, Image, RadioGroup, useToast } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import {
  openAccessControlModal,
  setEditMode,
  setTemplateData,
} from 'screens/admin/Objectives/GoalPreferences/slice/accessControlSlice';

import { MeshLogo } from 'assets/assetsV1/iconComponents';
import goalSettings1 from 'assets/images/illustrations/goalSettingsTemplates/goalSettings1.png';
import goalSettings2 from 'assets/images/illustrations/goalSettingsTemplates/goalSettings2.png';
import goalSettings3 from 'assets/images/illustrations/goalSettingsTemplates/goalSettings3.png';
import goalSettings4 from 'assets/images/illustrations/goalSettingsTemplates/goalSettings4.png';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import { convertDateToString10 } from 'constants/helper';

import { deleteTemplate } from 'services/admin';

import { Avatar } from 'uiToolkitV1/Avatar';
import { Radio } from 'uiToolkitV1/Radio';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { REACT_QUERY_GOALS_ACCESS_CONTROL } from '../constants';
import { goalPreferencesActions, useGoalPreferencesContext } from '../context/GoalPreferencesProvider';
import { getTemplateByIdV1 } from '../services';
import TemplateMenu from './TemplateMenu';

const GOAL_SETTINGS_TEMPLATES = [goalSettings1, goalSettings2, goalSettings3, goalSettings4];

const TruncatedTextLabel = styled(TextLabel)`
  display: -webkit-box;
  max-width: 200px;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const cloneTemplate = (templateData) => {
  const updatedTemplateData = {
    ...templateData,
    name: `Copy of ${templateData.name}`,
    type: 'CUSTOM',
    selected: false,
    id: null,
    uuid: null,
  };
  return updatedTemplateData;
};

const TemplateCard = ({ data, index }) => {
  const dispatch = useDispatch();

  const toast = useToast();

  const { changeTemplateConfig, goalPreferenceDispatch } = useGoalPreferencesContext();

  // limiting key between 0 to 3
  const imageKey = index % 4;

  const deleteTemplateHandler = async (templateId) => {
    try {
      await deleteTemplate(templateId);
      queryClient.invalidateQueries([REACT_QUERY_GOALS_ACCESS_CONTROL.allTemplates]);
      goalPreferenceDispatch({
        type: goalPreferencesActions.UPDATE_CONFIRMATION_MODAL,
        payload: {
          isOpen: false,
          text: '',
          heading: '',
          onSuccess: () => {},
        },
      });
      toast({
        title: 'Template deleted successfully',
        status: TOAST_STATUSES.SUCCESS,
        position: 'bottom-left',
      });
    } catch (error) {
      toast({
        title: 'Something went wrong',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
      console.error('Error deleting template', error);
    }
  };

  const templateId = data?.id;

  const getTemplateData = async ({ editMode, isClone }) => {
    try {
      const res = await getTemplateByIdV1(templateId);
      if (isClone) {
        const updatedData = cloneTemplate(res);
        dispatch(setTemplateData(updatedData));
        dispatch(setEditMode(editMode));
        dispatch(openAccessControlModal());
        return;
      }
      dispatch(setTemplateData(res));
      dispatch(setEditMode(editMode));
      dispatch(openAccessControlModal());
    } catch (error) {
      console.error('Error fetching template data');
    }
  };

  const templateOptions = useMemo(() => {
    if (data.type === 'DEFAULT') {
      return [
        {
          label: 'View',
          onClick: () => {
            getTemplateData({ editMode: false });
            // dispatch(setEditMode(false));
            // dispatch(setTemplateData(data));
            // dispatch(openAccessControlModal());
          },
        },

        {
          label: 'Make a Copy',
          onClick: () => {
            getTemplateData({ editMode: true, isClone: true });
          },
        },
      ];
    }
    return [
      {
        label: 'View',
        onClick: () => {
          getTemplateData({ editMode: false });
          // dispatch(setEditMode(false));
          // dispatch(setTemplateData(data));
          // dispatch(openAccessControlModal());
        },
      },
      {
        label: 'Edit',
        onClick: () => {
          getTemplateData({ editMode: true });
          // dispatch(setEditMode(true));
          // dispatch(setTemplateData(data));
          // dispatch(openAccessControlModal());
        },
      },
      {
        label: 'Make a Copy',
        onClick: () => {
          getTemplateData({ editMode: true, isClone: true });
        },
      },
      {
        label: 'Delete',
        onClick: () => {
          goalPreferenceDispatch({
            type: goalPreferencesActions.UPDATE_CONFIRMATION_MODAL,
            payload: {
              isOpen: true,
              heading: 'Delete this Template?',
              text: 'Are you sure you want to Delete this Template?',
              onSuccess: () => {
                deleteTemplateHandler(data?.id);
              },
            },
          });
        },
      },
    ];
  }, [data]);

  const queryClient = useQueryClient();

  const templatesData = queryClient.getQueryData([REACT_QUERY_GOALS_ACCESS_CONTROL.allTemplates]);

  const templateSelect = (templateId) => {
    queryClient.setQueryData(
      [REACT_QUERY_GOALS_ACCESS_CONTROL.allTemplates],
      templatesData.map((template) => {
        if (template.id === templateId) {
          return { ...template, selected: true };
        }
        return { ...template, selected: false };
      })
    );
  };

  return (
    <Box
      border={data?.selected ? `1px solid #104F95` : '1px solid #EBEFF3'}
      p="20px"
      borderRadius="4px"
      bgColor="#F5F7F9"
      // minWidth={'30rem'}
      cursor="pointer"
      onClick={(e) => {
        e.stopPropagation();
        changeTemplateConfig();
        templateSelect(data.id);
      }}
      h="190px"
    >
      <Flex justifyContent="space-between" h="100%">
        <Flex>
          <Box>
            <Image src={GOAL_SETTINGS_TEMPLATES[imageKey]} h="140px" w="140px" mr="12px" borderRadius="4px" />
          </Box>

          <Flex flexDirection="column" justifyContent="space-between">
            <TextBodyMedium bold>{data?.name}</TextBodyMedium>
            <TruncatedTextLabel color="text.g2" mt="4px">
              {data?.description}
            </TruncatedTextLabel>

            <Flex mt="auto" flexDirection="column">
              <Flex alignItems="center" py={3} columnGap={2}>
                {data?.type === 'DEFAULT' && <MeshLogo />}
                <TextLabel bold>{data?.type === 'DEFAULT' ? 'Mesh Recommended' : 'Custom Template'}</TextLabel>
              </Flex>
              <Flex>
                <TextLabel color="text.g2" mr="4px">
                  Last edited by
                </TextLabel>
                <Avatar src={data?.lastEditedBy?.profile?.profilePhoto} alt="Last updated by" mr="4px" />
                <TextLabel mr="4px">{data?.lastEditedBy?.metadata?.displayName}</TextLabel>
                <TextLabel color="text.g2"> on {convertDateToString10(data?.updatedTS)}</TextLabel>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        <Box>
          <Flex direction="column" height="100%" alignItems="center" justifyContent="space-between">
            <Box>
              <RadioGroup onChange={() => {}} value={data?.selected}>
                <Radio isChecked={Boolean(data?.selected)} value />
              </RadioGroup>
            </Box>

            <Box
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <TemplateMenu menuOptions={templateOptions} />
            </Box>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};

export default TemplateCard;
